import React from 'react'

interface TeamShotAverages {
  avg_shots: number | null
  avg_shots_on_target: number | null
  avg_shots_against: number | null
  avg_xg?: number | null
  avg_xg_against?: number | null
  // Venue-specific
  avg_shots_home?: number | null
  avg_shots_away?: number | null
  avg_shots_against_home?: number | null
  avg_shots_against_away?: number | null
  avg_shots_inside_box_home?: number | null
  avg_shots_inside_box_away?: number | null
}

interface ShotStatsCardProps {
  homeTeam: string
  awayTeam: string
  homeStats: TeamShotAverages
  awayStats: TeamShotAverages
  homeCrest?: string | null
  awayCrest?: string | null
  window?: 'season' | 'last10' | 'last5' | 'last3'
}

// A simple horizontal bar showing proportion
function ComparisonBar({
  homeValue,
  awayValue,
  maxValue,
  label,
  homeLabel,
  awayLabel,
}: {
  homeValue: number
  awayValue: number
  maxValue: number
  label: string
  homeLabel?: string
  awayLabel?: string
}) {
  if (maxValue === 0) return null

  const homePercent = (homeValue / maxValue) * 100
  const awayPercent = (awayValue / maxValue) * 100

  return (
    <div className="shot-comparison-row">
      <div className="shot-comparison-values">
        <span className="shot-value home">{homeValue.toFixed(1)}</span>
        <span className="shot-label">{label}</span>
        <span className="shot-value away">{awayValue.toFixed(1)}</span>
      </div>
      <div className="shot-comparison-bars">
        <div className="shot-bar-container home">
          <div
            className="shot-bar home-bar"
            style={{ width: `${homePercent}%` }}
            title={homeLabel}
          />
        </div>
        <div className="shot-bar-container away">
          <div
            className="shot-bar away-bar"
            style={{ width: `${awayPercent}%` }}
            title={awayLabel}
          />
        </div>
      </div>
    </div>
  )
}

// Shot efficiency indicator
function EfficiencyBadge({ accuracy, label }: { accuracy: number; label: string }) {
  const getColor = (acc: number) => {
    if (acc >= 40) return 'var(--green)'
    if (acc >= 30) return 'var(--yellow)'
    return 'var(--red)'
  }

  return (
    <div className="shot-efficiency-badge">
      <span className="efficiency-value" style={{ color: getColor(accuracy) }}>
        {accuracy.toFixed(0)}%
      </span>
      <span className="efficiency-label">{label}</span>
    </div>
  )
}

export function ShotStatsCard({
  homeTeam,
  awayTeam,
  homeStats,
  awayStats,
  homeCrest,
  awayCrest,
  window = 'season',
}: ShotStatsCardProps) {
  const homeShots = homeStats.avg_shots ?? 0
  const awayShots = awayStats.avg_shots ?? 0
  const homeSOT = homeStats.avg_shots_on_target ?? 0
  const awaySOT = awayStats.avg_shots_on_target ?? 0
  const homeShotsAgainst = homeStats.avg_shots_against ?? 0
  const awayShotsAgainst = awayStats.avg_shots_against ?? 0
  const homeXG = homeStats.avg_xg ?? null
  const awayXG = awayStats.avg_xg ?? null
  const homeXGAgainst = homeStats.avg_xg_against ?? null
  const awayXGAgainst = awayStats.avg_xg_against ?? null

  // Calculate shot accuracy percentages
  const homeAccuracy = homeShots > 0 ? (homeSOT / homeShots) * 100 : 0
  const awayAccuracy = awayShots > 0 ? (awaySOT / awayShots) * 100 : 0

  // Max values for scaling bars
  const maxShots = Math.max(homeShots, awayShots, homeShotsAgainst, awayShotsAgainst, 15)
  const maxSOT = Math.max(homeSOT, awaySOT, 8)
  const maxXG = Math.max(homeXG ?? 0, awayXG ?? 0, homeXGAgainst ?? 0, awayXGAgainst ?? 0, 2)

  // Venue-specific stats
  const homeVenueShots = homeStats.avg_shots_home ?? homeShots
  const awayVenueShots = awayStats.avg_shots_away ?? awayShots

  const windowLabel = {
    season: 'Season Avg',
    last10: 'Last 10 Avg',
    last5: 'Last 5 Avg',
    last3: 'Last 3 Avg',
  }[window]

  return (
    <div className="shot-stats-card">
      <div className="shot-stats-header">
        <h4>Shot Analysis</h4>
        <span className="shot-stats-window">{windowLabel}</span>
      </div>

      {/* Team header */}
      <div className="shot-teams-header">
        <div className="shot-team home">
          {homeCrest && <img src={homeCrest} alt="" className="shot-team-crest" />}
          <span className="shot-team-name">{homeTeam}</span>
        </div>
        <div className="shot-team away">
          <span className="shot-team-name">{awayTeam}</span>
          {awayCrest && <img src={awayCrest} alt="" className="shot-team-crest" />}
        </div>
      </div>

      {/* Main stats comparison */}
      <div className="shot-stats-grid">
        <ComparisonBar
          homeValue={homeShots}
          awayValue={awayShots}
          maxValue={maxShots}
          label="Shots/Game"
        />
        <ComparisonBar
          homeValue={homeSOT}
          awayValue={awaySOT}
          maxValue={maxSOT}
          label="On Target/Game"
        />
        <ComparisonBar
          homeValue={homeShotsAgainst}
          awayValue={awayShotsAgainst}
          maxValue={maxShots}
          label="Shots Faced/Game"
        />

        {/* xG comparison if available */}
        {(homeXG != null || awayXG != null) && (
          <ComparisonBar
            homeValue={homeXG ?? 0}
            awayValue={awayXG ?? 0}
            maxValue={maxXG}
            label="xG/Game"
          />
        )}
        {(homeXGAgainst != null || awayXGAgainst != null) && (
          <ComparisonBar
            homeValue={homeXGAgainst ?? 0}
            awayValue={awayXGAgainst ?? 0}
            maxValue={maxXG}
            label="xGA/Game"
          />
        )}
      </div>

      {/* Shot accuracy section */}
      <div className="shot-accuracy-section">
        <div className="shot-accuracy-header">Shot Accuracy</div>
        <div className="shot-accuracy-grid">
          <EfficiencyBadge accuracy={homeAccuracy} label={homeTeam} />
          <div className="shot-accuracy-vs">vs</div>
          <EfficiencyBadge accuracy={awayAccuracy} label={awayTeam} />
        </div>
      </div>

      {/* Venue-specific insights */}
      {(homeVenueShots !== homeShots || awayVenueShots !== awayShots) && (
        <div className="shot-venue-section">
          <div className="shot-venue-header">Venue Form</div>
          <div className="shot-venue-grid">
            <div className="shot-venue-stat">
              <span className="shot-venue-value">{homeVenueShots.toFixed(1)}</span>
              <span className="shot-venue-label">{homeTeam} (H)</span>
            </div>
            <div className="shot-venue-stat">
              <span className="shot-venue-value">{awayVenueShots.toFixed(1)}</span>
              <span className="shot-venue-label">{awayTeam} (A)</span>
            </div>
          </div>
        </div>
      )}

      {/* Predicted shots summary */}
      <div className="shot-prediction-section">
        <div className="shot-prediction-header">Expected Total Shots</div>
        <div className="shot-prediction-value">
          {(homeVenueShots + awayVenueShots).toFixed(1)}
          <span className="shot-prediction-breakdown">
            ({homeVenueShots.toFixed(1)} + {awayVenueShots.toFixed(1)})
          </span>
        </div>
      </div>
    </div>
  )
}
